// react
import { Dispatch, SetStateAction, useContext, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	Box,
	CircularProgress,
	Divider,
	FormControl,
	FormHelperText,
	Grid,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from '@mui/material';

// icons
import { AccountTree as IconActivity } from '@mui/icons-material';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';
import { ContextSpendPlan } from '../../pages/ProjectV2/ProjectV2.context';

// forms
import { FormManageContract } from '../FormManageContract/FormManageContract';

// components
import {
	CustomFieldsFormSection,
	validateCustomFields,
} from '../../ui/CustomFieldsFormSection/CustomFieldsFormSection';
import { CommitmentTypeSelector } from '../../ui/CommitmentTypeSelector/CommitmentTypeSelector';
import { FilePicker } from '../../fields/FilePicker/FilePicker';

// services
import { uploadAiDocument } from '../../../services/aiApi';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// types
import { Contract, Entity, V2Contract, V2ContractPayload } from '../../../@types';

// utils
import { formatCurrency } from '../../../utils/currency';
import { getIds } from '../../../utils/objects';

// Props

export interface FormManageContractV2Props {
	contract?: V2Contract;
	entity?: Entity;
	onClose?: () => void;
	onSuccess?: (contractId?: number) => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

// Component

/**
 * Unified V2 contract form (create and edit).
 *
 * Wraps the V1 `FormManageContract` (legal contract fields: number, suppliers,
 * amounts, signing date, description, documents) and adds a "Spend plan
 * assignment" section (activity, full vs. partial commitment, commitment in
 * work-plan currency, commitment in local currency) plus the project's custom
 * contract fields. Custom fields are always shown, regardless of whether an
 * activity is selected.
 *
 * Submit behavior:
 *  - Create: posts the legal contract to /contracts (V1). If an activity was
 *    selected, posts /v2/contracts to "promote" the new row with the activity,
 *    commitment fields and custom data. If no activity is selected but custom
 *    data was entered, PATCHes /v2/contracts/:id with just the custom data so
 *    it persists on the "Unassigned" contract.
 *  - Edit: PATCHes the legal fields via /contracts/:id, then PATCHes the V2
 *    spend-plan fields (activity, commitment, custom data) via /v2/contracts/:id.
 */
export const FormManageContractV2 = ({
	contract,
	entity,
	onClose,
	onSuccess,
	setIsLoading,
}: FormManageContractV2Props) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { activities, createContract, updateContract } = useContext(ContextSpendPlan);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// Edit mode: when an existing V2 contract is supplied we prefill the legal
	// fields (via the mapped V1 contract below) and the spend-plan fields, and
	// PATCH instead of POST on submit.
	const isEdit = Boolean(contract);

	// The classification (categories / components / expense types) lives in the
	// V1 contract_* junction tables and is NOT carried by the `v_contract` view
	// that backs the V2 contract objects, so an editing contract arrives here
	// without it. Fetch the ids from the V1 `/contracts/:id` endpoint (which
	// does populate them) so the inner form can prefill the multi-selects. The
	// inner form derives its defaults once at mount, so we hold rendering until
	// this resolves in edit mode (see the loading gate at the end of render).
	const [classification, setClassification] = useState<{
		categories: number[];
		components: number[];
		expenseTypes: number[];
	} | null>(isEdit ? null : { categories: [], components: [], expenseTypes: [] });

	useFetch({
		isEnabled: isEdit && Boolean(contract?.id),
		url: `${process.env.REACT_APP_API_URL}/contracts/${contract?.id}?fields=categories,components,expenseTypes`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			const data = Array.isArray(res?.data) ? res.data[0] : res?.data;
			setClassification({
				categories: getIds(data?.categories || []),
				components: getIds(data?.components || []),
				expenseTypes: getIds(data?.expenseTypes || []),
			});
		},
		onError: () => {
			// Fall back to empty selections rather than blocking the edit form.
			setClassification({ categories: [], components: [], expenseTypes: [] });
		},
	});

	// The inner V1 `FormManageContract` is prefilled from a V1 `Contract`. The
	// V2 contract now exposes the same field names (num / description /
	// dateSigning), so the mapping is a straight passthrough. The canonical id is
	// the V1 contract row id, used for the PATCH /contracts/:id call.
	const mappedContract: Contract | undefined = contract
		? ({
				id: contract.id,
				num: contract.num || '',
				description: contract.description || '',
				dateSigning: contract.dateSigning || '',
				entities: contract.entities.map((e) => ({ id: e.id, name: e.name })),
				amounts: contract.amounts.map((a) => ({
					id: a.id,
					amount: a.amount,
					currency: a.currency,
					activityId: a.activityId,
					transactions: [],
				})),
				categories: classification?.categories || [],
				components: classification?.components || [],
				expenseTypes: classification?.expenseTypes || [],
			} as unknown as Contract)
		: undefined;

	// V2 field state (optional spend-plan assignment), prefilled in edit mode.
	const [activityId, setActivityId] = useState<number | ''>(contract?.planningActivity || '');
	const [commitmentType, setCommitmentType] = useState<'full' | 'partial'>(
		contract?.commitmentAmount != null ? 'partial' : 'full'
	);
	const [commitmentAmount, setCommitmentAmount] = useState<string>(
		contract?.commitmentAmount != null ? String(contract.commitmentAmount) : ''
	);
	const [commitmentAmountLocal, setCommitmentAmountLocal] = useState<string>(
		contract?.commitmentAmountLocal != null ? String(contract.commitmentAmountLocal) : ''
	);
	const [customData, setCustomData] = useState<Record<string, unknown>>(contract?.customData || {});
	const [v2Files, setV2Files] = useState<File[]>([]);
	const [v2Errors, setV2Errors] = useState<{
		activityId?: string;
		commitmentAmount?: string;
		commitmentAmountLocal?: string;
		custom?: string;
	}>({});

	// derived
	const selectedActivity = activityId ? activities.find((a) => a.id === activityId) || null : null;
	const activityCurrency = selectedActivity?.currency || 'USD';
	const localCurrency = project?.currencies?.find((c) => c.type === 'LOCAL')?.currency || null;

	const customContractColumns =
		(project?.customFieldDefinitions || []).find((d) => d.entityType === 'contract')?.definitions ||
		[];

	// handlers
	const handleActivityChange = (e: SelectChangeEvent<number | ''>) => {
		const value = e.target.value;
		setActivityId(typeof value === 'string' && value === '' ? '' : Number(value));
		setV2Errors((prev) => ({ ...prev, activityId: undefined }));
	};

	const handleCommitmentTypeChange = (next: 'full' | 'partial') => {
		setCommitmentType(next);
		if (next === 'full') {
			setCommitmentAmount('');
			setCommitmentAmountLocal('');
			setV2Errors((prev) => ({
				...prev,
				commitmentAmount: undefined,
				commitmentAmountLocal: undefined,
			}));
		}
	};

	const handleCustomFieldChange = (columnId: string, value: unknown) => {
		setCustomData((prev) => ({ ...prev, [columnId]: value }));
		setV2Errors((prev) => ({ ...prev, custom: undefined }));
	};

	// Validation: commitment fields are enforced only when an activity is
	// selected (activity itself is optional — leaving it blank creates an
	// "Unassigned" contract). Custom fields are always validated since they
	// are always shown.
	const validateV2 = (): boolean => {
		const errors: typeof v2Errors = {};

		if (activityId) {
			if (commitmentType === 'partial') {
				if (commitmentAmount === '') {
					errors.commitmentAmount = 'Enter the partial commitment amount';
				} else {
					const n = parseFloat(commitmentAmount);
					if (isNaN(n) || n <= 0) {
						errors.commitmentAmount = 'Amount must be greater than 0';
					}
					// The activity-budget overrun is no longer a client-side block:
					// the server performs a currency-aware (apples-to-apples) check
					// and, on a genuine same-currency overrun, still saves the link
					// and returns a non-blocking warning that we surface in the banner.
				}
				if (localCurrency) {
					if (commitmentAmountLocal === '') {
						errors.commitmentAmountLocal = `Required when partial commitment is set (in ${localCurrency})`;
					} else {
						const n = parseFloat(commitmentAmountLocal);
						if (isNaN(n) || n <= 0) {
							errors.commitmentAmountLocal = 'Amount must be greater than 0';
						}
					}
				}
			}
		}

		const missingFields = validateCustomFields(customContractColumns, customData);
		if (missingFields.length > 0) {
			errors.custom = `Required custom fields: ${missingFields.join(', ')}`;
		}

		setV2Errors(errors);
		return Object.keys(errors).length === 0;
	};

	// After-write hook (fires on both create and edit). Persists the V2
	// spend-plan fields (activity, commitment, custom data) on the contract row
	// and uploads any V2 documents to the AI/bronze container.
	//
	// Persistence paths:
	//  - Edit: PATCH /v2/contracts/:id with the changed linkage + custom fields.
	//  - Create + activity: POST /v2/contracts to promote the new contract row.
	//  - Create, no activity but custom data present: PATCH /v2/contracts/:id
	//    with just the custom data (POST requires an activity, PATCH does not),
	//    so custom fields are saved even for Unassigned contracts.
	const handleAfterCreate = async (newContractId: number) => {
		// Validate V2 fields now (we can't block FormManageContract's submit from
		// here, so on failure we throw to surface in the form's banner).
		if (!validateV2()) {
			throw new Error(
				isEdit
					? 'Contract was saved but spend-plan fields are invalid. Fix the highlighted fields and try again.'
					: 'Contract was created but spend-plan assignment fields are invalid. ' +
							'Fix the highlighted fields and use the "Link to activity" action on the contract row to complete the assignment.'
			);
		}

		if (isEdit && contract) {
			const payload: Partial<V2ContractPayload> = {
				contractId: contract.id,
				customData,
				projectId: project?.id ?? null,
			};
			if (activityId) {
				payload.activityId = activityId as number;
				payload.commitmentAmount =
					commitmentType === 'partial' && commitmentAmount !== ''
						? parseFloat(commitmentAmount)
						: null;
				payload.commitmentAmountLocal =
					commitmentType === 'partial' && commitmentAmountLocal !== ''
						? parseFloat(commitmentAmountLocal)
						: null;
			}

			const res = await updateContract(contract.id, payload);
			if (res?.error) {
				throw new Error(res.error.message, {
					cause: { id: res.error.id, res },
				});
			}
			if (res?.warning) {
				setBanner({
					hasClose: true,
					severity: 'warning',
					message: res.warning.message,
				});
			}
		} else if (activityId) {
			const payload: V2ContractPayload = {
				activityId: activityId as number,
				contractId: newContractId,
				commitmentAmount:
					commitmentType === 'partial' && commitmentAmount !== ''
						? parseFloat(commitmentAmount)
						: null,
				commitmentAmountLocal:
					commitmentType === 'partial' && commitmentAmountLocal !== ''
						? parseFloat(commitmentAmountLocal)
						: null,
				customData,
				projectId: project?.id ?? null,
			};

			const res = await createContract(payload);
			if (res?.error) {
				// Re-throw with structured `cause` so FormManageContract's catch
				// can route this through its error-id mapping (e.g. show a
				// translated message for CONTRACTS_EXCEED_ACTIVITY_BUDGET) and
				// preserve the server message as the visible default.
				throw new Error(res.error.message, {
					cause: { id: res.error.id, res },
				});
			}
			if (res?.warning) {
				// Currency-aware budget overrun: the link was still saved, so we
				// don't throw — surface it as a non-blocking banner instead.
				setBanner({
					hasClose: true,
					severity: 'warning',
					message: res.warning.message,
				});
			}
		} else if (Object.keys(customData).length > 0) {
			// Unassigned contract with custom data: PATCH the custom fields onto
			// the freshly created contract row (POST /v2/contracts is not used here
			// because it requires an activity).
			const res = await updateContract(newContractId, { customData });
			if (res?.error) {
				throw new Error(res.error.message, {
					cause: { id: res.error.id, res },
				});
			}
		}

		// Upload V2 documents (AI/bronze container) tagged to the new contract.
		if (v2Files.length > 0) {
			const failed: string[] = [];
			await Promise.all(
				v2Files.map(async (file) => {
					try {
						await uploadAiDocument({
							file,
							doc_type: 'contract',
							contract_id: String(newContractId),
							project_id: project?.id,
							source_type: 'contract',
							source_id: newContractId,
							tag: `contract:${newContractId}`,
						});
					} catch (err) {
						failed.push(`${file.name}: ${(err as Error).message}`);
					}
				})
			);
			if (failed.length > 0) {
				throw new Error(
					`Contract created, but some documents failed to upload:\n${failed.join('\n')}`,
					{ cause: { id: 'V2_DOCUMENT_UPLOAD_FAILED' } }
				);
			}
		}
	};

	// V2 fields slot rendered inside the V1 form
	const v2Fields = (
		<>
			<Grid item xs={12}>
				<Divider sx={{ my: 1 }} />
				<Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>
					{t('spendPlanAssignment', 'Spend Plan Assignment')}
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					{t(
						'spendPlanAssignmentHelp',
						'Optionally link this contract to a planning activity now. You can also leave this blank and link it later from the contracts listing.'
					)}
				</Typography>
			</Grid>

			<Grid item xs={12}>
				<FormControl error={Boolean(v2Errors.activityId)} fullWidth>
					<InputLabel>{t('activity', 'Activity')}</InputLabel>
					<Select<number | ''>
						label={t('activity', 'Activity')}
						name="activityId"
						value={activityId}
						onChange={handleActivityChange}
						startAdornment={
							<InputAdornment position="start">
								<IconActivity color="action" />
							</InputAdornment>
						}>
						<MenuItem value="">
							<em>{t('noActivityUnassigned', 'No activity (create as Unassigned)')}</em>
						</MenuItem>
						{activities.map((a) => (
							<MenuItem key={a.id} value={a.id}>
								<Box>
									<Typography variant="body2">{a.name}</Typography>
									<Typography variant="caption" color="text.secondary">
										{formatCurrency({ currency: a.currency, value: a.amount })} planned
									</Typography>
								</Box>
							</MenuItem>
						))}
					</Select>
					{v2Errors.activityId && <FormHelperText>{v2Errors.activityId}</FormHelperText>}
				</FormControl>
			</Grid>

			{activityId && (
				<>
					<Grid item xs={12}>
						<Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
							{t('commitmentAmount', 'Commitment Amount')}
						</Typography>
						<CommitmentTypeSelector value={commitmentType} onChange={handleCommitmentTypeChange} />
						{commitmentType === 'partial' && (
							<Grid container spacing={2} sx={{ mt: 0.5 }}>
								<Grid item xs={12} md={localCurrency ? 6 : 12}>
									<TextField
										fullWidth
										label={t('amount', 'Amount')}
										type="number"
										value={commitmentAmount}
										onChange={(e) => {
											setCommitmentAmount(e.target.value);
											setV2Errors((prev) => ({
												...prev,
												commitmentAmount: undefined,
											}));
										}}
										error={Boolean(v2Errors.commitmentAmount)}
										helperText={v2Errors.commitmentAmount}
										required
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">{activityCurrency}</InputAdornment>
											),
										}}
									/>
								</Grid>
								{localCurrency && (
									<Grid item xs={12} md={6}>
										<TextField
											fullWidth
											label={t('amountLocal', 'Amount (Local)')}
											type="number"
											value={commitmentAmountLocal}
											onChange={(e) => {
												setCommitmentAmountLocal(e.target.value);
												setV2Errors((prev) => ({
													...prev,
													commitmentAmountLocal: undefined,
												}));
											}}
											error={Boolean(v2Errors.commitmentAmountLocal)}
											helperText={v2Errors.commitmentAmountLocal}
											required
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">{localCurrency}</InputAdornment>
												),
											}}
										/>
									</Grid>
								)}
							</Grid>
						)}
					</Grid>
				</>
			)}

			{customContractColumns.length > 0 && (
				<Grid item xs={12}>
					<CustomFieldsFormSection
						columns={customContractColumns}
						customData={customData}
						onChange={handleCustomFieldChange}
					/>
					{v2Errors.custom && (
						<Typography variant="caption" color="error">
							{v2Errors.custom}
						</Typography>
					)}
				</Grid>
			)}
		</>
	);

	// V2 file picker — rendered in place of the V1 `FilePicker` (same slot,
	// just above the description field) but uploads to the AI/bronze
	// container via `uploadAiDocument` in `handleAfterCreate`.
	const v2FilePicker = <FilePicker onChange={(_e, filesSelected) => setV2Files(filesSelected)} />;

	// Hold rendering until the contract's classification has been fetched in
	// edit mode. The inner form snapshots its prefill defaults at mount, so
	// mounting it before the ids arrive would leave the multi-selects blank.
	if (isEdit && classification === null) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<FormManageContract
			contract={mappedContract}
			entity={entity}
			requestSource="v2"
			extraFields={v2Fields}
			filePickerSlot={v2FilePicker}
			onAfterCreate={handleAfterCreate}
			onClose={onClose}
			onSuccess={onSuccess}
			setIsLoading={setIsLoading}
		/>
	);
};
